import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { IMPACT_STATS } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const accentClass = {
  coral: 'from-[#F55B26] to-[#FF8A3D]',
  blue: 'from-[#2196F3] to-[#42A5F5]',
  magenta: 'from-[#EC407A] to-[#F06292]',
  purple: 'from-[#8E24AA] to-[#AB47BC]',
  green: 'from-[#7CB342] to-[#9CCC65]',
  amber: 'from-[#FFB347] to-[#FFC978]',
  teal: 'from-[#0B3B44] to-[#1B6572]',
};

const Impact = () => {
  const [stats, setStats] = useState(IMPACT_STATS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/stats`);
        if (cancelled) return;
        const s = res.data;
        // Only override live-tracked stats; keep the rest from mock
        // Always show foundation-level impact numbers; only replace when meaningful
        const showLive = s.meals_funded >= 100;
        const updated = [
          { value: '250,000+', label: 'Meals per Annum Goal', accent: 'coral' },
          { value: '600', label: 'Meals Served Daily', accent: 'blue' },
          { value: '6', label: 'Communities Served', accent: 'magenta' },
          showLive
            ? { value: `R${Math.round(s.total_amount_zar).toLocaleString()}`, label: 'Raised Online', accent: 'purple' }
            : { value: '1M+', label: 'Plates Served Since Inception', accent: 'purple' },
        ];
        setStats(updated);
      } catch {
        // keep mocked stats
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <section id="impact" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1628428799437-d886d7d2e9b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxzb3VwJTIwa2l0Y2hlbnxlbnwwfHx8fDE3ODMwNTk3MDR8MA&ixlib=rb-4.1.0&q=85"
          alt="Community feeding"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B3B44]/95 via-[#0B3B44]/90 to-[#0B3B44]/80" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="space-y-5 text-white">
            <div className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur border border-white/20 text-white font-semibold text-xs uppercase tracking-widest rounded-full">
              Our Impact
            </div>
            <h2 className="text-4xl lg:text-5xl font-extrabold leading-tight">
              Real numbers. <span className="text-[#FFB347]">Real lives.</span>
            </h2>
            <p className="text-lg text-white/85 leading-relaxed">
              Behind every meal is a story. Behind every donation is a family fed, a child
              given hope, a community strengthened. Here is what we&apos;ve achieved together.
            </p>
            <blockquote className="pl-5 border-l-4 border-[#F55B26] mt-6">
              <p className="italic text-white/90 text-lg">
                &ldquo;Great things are done by a series of small things brought together.&rdquo;
              </p>
              <footer className="text-sm text-[#FFB347] font-semibold mt-2">
                &mdash; Vincent van Gogh
              </footer>
            </blockquote>
          </div>

          <div className="grid grid-cols-2 gap-5">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 p-7 hover:bg-white/15 transition-all hover:-translate-y-1"
              >
                <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${accentClass[stat.accent]} opacity-30 blur-2xl group-hover:opacity-50 transition-opacity`} />
                <div className="relative">
                  <div className="text-4xl lg:text-5xl font-extrabold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-white/80 uppercase tracking-wider font-medium">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Impact;
